import type { PricePoint } from "@/lib/data/types";
import { CHART_RANGES, type ChartRangeLabel } from "@/components/charts/range-selector";
import { standardDeviation } from "@/lib/stats";

/** Navy (eigene Aktie) + sieben weitere, im Projekt bereits etablierte Akzentfarben fuer
 * Mehrlinien-Vergleichscharts - gemeinsam genutzt von peer-compare.tsx (Aktien-Duell) und
 * competitor-price-race-chart.tsx (Konkurrenzanalyse), statt zweimal dieselbe Palette zu
 * pflegen. */
export const PEER_COLORS = [
  "#C6A15B",
  "#3F7A5B",
  "#B4453B",
  "#26407F",
  "#6B7280",
  "#8B5CF6",
  "#0E7490",
  "#A16207",
];

/** Indexiert eine Kursreihe auf 100 am ersten Datenpunkt, damit Aktien mit
 * unterschiedlichem absolutem Kursniveau optisch vergleichbar werden. Gemeinsam genutzt
 * von peer-compare.tsx und competitor-price-race-chart.tsx. */
export function indexTo100(points: PricePoint[]): Map<string, number> {
  const map = new Map<string, number>();
  if (points.length === 0) return map;
  const base = points[0].close;
  if (!base) return map;
  for (const p of points) {
    map.set(p.date, (p.close / base) * 100);
  }
  return map;
}

/** Filtert Tages-/Wochendaten auf die letzten N Jahre ab heute (nicht ab dem juengsten
 * Datenpunkt wie bei `filterYearRange` - Kursdaten koennen einige Tage alt sein). */
export function filterDateRange(data: PricePoint[], range: ChartRangeLabel): PricePoint[] {
  const config = CHART_RANGES.find((r) => r.label === range);
  if (!config || config.years === null || data.length === 0) return data;
  const cutoff = new Date();
  cutoff.setFullYear(cutoff.getFullYear() - config.years);
  const cutoffStr = cutoff.toISOString().slice(0, 10);
  const sliced = data.filter((p) => p.date >= cutoffStr);
  return sliced.length > 1 ? sliced : data;
}

/**
 * Duennt sehr lange Zeitreihen auf hoechstens `maxPoints` aus, bevor sie an Recharts
 * gehen (Performance - bei Jahrzehnten taeglicher Kursdaten erzeugt Recharts sonst
 * tausende SVG-Knoten pro Range-Wechsel, v.a. im Volumen-Balken-Subplot). 1J bleibt mit
 * ~250 Handelstagen ohnehin unter der Schwelle.
 *
 * Largest-Triangle-Three-Buckets statt stures Ausduennen im festen Abstand ("jeden n-ten
 * Punkt nehmen"): die alte Stride-Methode konnte genau den Tageshoch-/Tiefpunkt eines
 * Kurssprungs uebergehen und den Chart faelschlich glatter aussehen lassen, als der
 * Kursverlauf tatsaechlich war. LTTB waehlt je Bucket den Punkt, der die groesste
 * Dreiecksflaeche zum vorherigen gewaehlten Punkt und dem Mittelwert des naechsten Buckets
 * aufspannt - das erhaelt sichtbare lokale Extremwerte statt sie zu verschlucken. Erster
 * und letzter Punkt bleiben immer erhalten.
 */
export function downsample<T>(data: T[], maxPoints: number, valueOf: (item: T) => number): T[] {
  if (data.length <= maxPoints) return data;
  if (maxPoints <= 2) {
    return data.length > 0 ? [data[0], data[data.length - 1]] : [];
  }

  const sampled: T[] = [data[0]];
  const bucketSize = (data.length - 2) / (maxPoints - 2);
  let a = 0;

  for (let i = 0; i < maxPoints - 2; i++) {
    const avgRangeStart = Math.floor((i + 1) * bucketSize) + 1;
    const avgRangeEnd = Math.min(Math.floor((i + 2) * bucketSize) + 1, data.length);
    const avgRangeLength = Math.max(1, avgRangeEnd - avgRangeStart);

    let avgX = 0;
    let avgY = 0;
    for (let j = avgRangeStart; j < avgRangeEnd; j++) {
      avgX += j;
      avgY += valueOf(data[j]);
    }
    avgX /= avgRangeLength;
    avgY /= avgRangeLength;

    const rangeStart = Math.floor(i * bucketSize) + 1;
    const rangeEnd = Math.min(Math.floor((i + 1) * bucketSize) + 1, data.length - 1);

    const pointAX = a;
    const pointAY = valueOf(data[a]);

    let maxArea = -1;
    let maxAreaIndex = rangeStart;
    for (let j = rangeStart; j < rangeEnd; j++) {
      const area = Math.abs(
        (pointAX - avgX) * (valueOf(data[j]) - pointAY) - (pointAX - j) * (avgY - pointAY),
      );
      if (area > maxArea) {
        maxArea = area;
        maxAreaIndex = j;
      }
    }
    sampled.push(data[maxAreaIndex]);
    a = maxAreaIndex;
  }

  sampled.push(data[data.length - 1]);
  return sampled;
}

/**
 * Waehlt Nachkommastellen fuer Kurs-Achsenbeschriftungen anhand der Preis-Groessenordnung.
 * Ein fixes `maximumFractionDigits: 0` liess bei niedrigpreisigen Aktien (z.B. 3-8 $)
 * mehrere benachbarte Ticks identisch aussehen ("5 $, 5 $, 5 $"), obwohl sich die
 * zugrunde liegenden Werte unterschieden.
 */
export function pricePrecisionDigits(referencePrice: number | undefined): number {
  if (referencePrice === undefined || !Number.isFinite(referencePrice) || referencePrice <= 0) return 0;
  if (referencePrice < 10) return 2;
  if (referencePrice < 100) return 1;
  return 0;
}

/**
 * Recharts waehlt Tick-INDIZES nach Pixelabstand, nicht nach dem formatierten Label -
 * bei mehrjaehrigen Zeitraeumen mit dichten Datenpunkten fuehrte das dazu, dass dieselbe
 * Jahreszahl mehrfach hintereinander auftauchte. Stattdessen explizit genau einen Tick
 * pro Kalenderjahr waehlen (bei sehr langen Zeitraeumen ausgeduennt). Gemeinsam genutzt
 * von price-history-chart.tsx und technical-chart.tsx, die beide Jahres-Ticks auf einer
 * taeglichen/woechentlichen Kurs-Zeitreihe brauchen - nur eine Implementierung verhindert,
 * dass der Fix in einem der beiden Charts wieder auseinanderlaeuft.
 */
/**
 * Kern-Dezimierungslogik von `computeYearTicks`, hier separat exportiert fuer Achsen, die
 * bereits rohe Kalenderjahres-Zahlen statt Datums-Strings fuehren (z.B. eine kategoriale
 * Jahres-Achse wie in dividend-yield-timeline-chart.tsx) - vermeidet eine zweite,
 * driftende Kopie derselben Schritt/Modulo/Letztes-Jahr-erzwingen-Logik. Erwartet
 * aufsteigend sortierte, eindeutige Jahre.
 */
export function decimateYears(years: number[], maxTicks = 8): number[] {
  if (years.length === 0) return [];
  const step = Math.max(1, Math.ceil(years.length / maxTicks));
  const picked = years.filter((_, i) => i % step === 0);
  // Neuestes Jahr immer als Tick zeigen (sonst wirkt der Chart am rechten Rand
  // "abgeschnitten"/veraltet, wenn die Modulo-Auswahl zufaellig kurz davor endet).
  const lastYear = years[years.length - 1];
  if (picked[picked.length - 1] !== lastYear) picked[picked.length - 1] = lastYear;
  return picked;
}

export function computeYearTicks(data: { date: string }[], maxTicks = 8): string[] {
  const firstOfYear = new Map<number, string>();
  for (const point of data) {
    const year = new Date(point.date).getUTCFullYear();
    if (!firstOfYear.has(year)) firstOfYear.set(year, point.date);
  }
  const years = [...firstOfYear.keys()].sort((a, b) => a - b);
  return decimateYears(years, maxTicks).map((y) => firstOfYear.get(y)!);
}

/** Gleiche Logik wie `computeYearTicks`, nur auf Monatsebene fuer die 1J-Ansicht (dort
 * zeigt der Achsenformatter "Monat Jahr" statt nur das Jahr) - ohne explizite Ticks
 * tauchte derselbe Monat/Jahr aus demselben Pixelabstand-Grund mehrfach hintereinander auf. */
export function computeMonthTicks(data: { date: string }[], maxTicks = 8): string[] {
  const firstOfMonth = new Map<string, string>();
  for (const point of data) {
    const d = new Date(point.date);
    const key = `${d.getUTCFullYear()}-${d.getUTCMonth()}`;
    if (!firstOfMonth.has(key)) firstOfMonth.set(key, point.date);
  }
  const keys = [...firstOfMonth.keys()];
  if (keys.length === 0) return [];
  const step = Math.max(1, Math.ceil(keys.length / maxTicks));
  const picked = keys.filter((_, i) => i % step === 0);
  const lastKey = keys[keys.length - 1];
  if (picked[picked.length - 1] !== lastKey) picked[picked.length - 1] = lastKey;
  return picked.map((k) => firstOfMonth.get(k)!);
}

/**
 * Gemeinsam von price-history-chart.tsx UND technical-chart.tsx genutzt (vorher hatte
 * nur der erste Chart eine range-abhaengige Achse, der zweite zeigte im 1J-Zeitraum
 * durchgehend nur Jahres-Ticks statt Monats-Ticks - exakt die Art Achsenbeschriftungs-
 * Bug, die hier eigentlich schon einmal behoben wurde, nur eben nur an einer der beiden
 * Stellen). Eine Implementierung fuer "welche Ticks + welches Format je nach Zeitraum"
 * verhindert, dass beide Charts wieder auseinanderlaufen.
 */
export function computePriceAxisTicks(
  data: { date: string }[],
  range: ChartRangeLabel,
  maxTicks = 8,
): string[] {
  return range === "1J" ? computeMonthTicks(data, maxTicks) : computeYearTicks(data, maxTicks);
}

export function formatPriceAxisTick(dateValue: string | number, range: ChartRangeLabel): string {
  const date = new Date(dateValue);
  if (range === "1J") {
    return date.toLocaleDateString("de-DE", { month: "short", year: "2-digit" });
  }
  return String(date.getUTCFullYear());
}

/**
 * Kursreihen mischen woechentliche (aeltere Jahre) und taegliche (juengere Jahre)
 * Datenpunkte (siehe yahoo-provider.ts -> getPriceHistory), die Punktdichte je Kalenderjahr
 * ist also stark ungleich. Ein Recharts-XAxis mit einem String-`dataKey` verteilt Ticks
 * per Definition GLEICHMAESSIG NACH INDEX, nicht nach echter Zeit - bei ungleicher
 * Punktdichte wanderten dadurch z.B. "2006"/"2009" (duenne Wochendaten) sichtbar zu nah
 * zusammen an den linken Rand, waehrend dichte juengere Jahre ueberproportional viel Platz
 * bekamen. Fix: echtes Zeit-Feld (Unix-Millisekunden) ergaenzen und die Achse als
 * `type="number"` fuehren - Positionen skalieren dann linear mit echter verstrichener Zeit.
 */
export function withTimeField<T extends { date: string }>(data: T[]): (T & { time: number })[] {
  return data.map((point) => ({ ...point, time: Date.parse(point.date) }));
}

/**
 * Kurzfristige Volatilitaets-Kennzahl (backlog.md -> "Kurzfristige Volatilitaets-
 * Kennzahl", Dashboard/Alltag-Katalog) - Standardabweichung der taeglichen einfachen
 * Renditen ueber die letzten 30 Handelstage. Rein deskriptiv, keine "je hoeher desto
 * besser/schlechter"-Skala (siehe scoring-Kommentar in category-tabs.tsx zu genau
 * dieser Eigenschaft von Volatilitaet). Nutzt IMMER die zeitlich letzten Punkte der
 * uebergebenen Historie, unabhaengig von einer evtl. vom Nutzer gewaehlten Chart-
 * Zeitraum-Auswahl - "wie stark schwankt die Aktie GERADE" soll sich nicht aendern,
 * nur weil jemand den Chart auf "Max" umstellt.
 */
export function compute30DayVolatility(priceHistory: { date: string; close: number }[]): number | undefined {
  const recent = [...priceHistory].sort((a, b) => a.date.localeCompare(b.date)).slice(-31);
  const returns: number[] = [];
  for (let i = 1; i < recent.length; i++) {
    const prev = recent[i - 1].close;
    const curr = recent[i].close;
    if (prev > 0) returns.push((curr - prev) / prev);
  }
  return standardDeviation(returns);
}

/**
 * Tastatur-Alternative zum Klick-Zieh-Zoom (backlog.md -> Accessibility-Audit, "Chart-
 * Klick-Zieh-Zoom hat keine Tastatur-Alternative zum ERSTELLEN einer Zoom-Auswahl") -
 * reine Funktion, von lib/use-drag-zoom.ts genutzt, damit sich die Auswahl-Mathematik
 * unabhaengig vom Tastatur-Event-Handling selbst testen laesst. Jeder Tastendruck
 * erweitert die Auswahl um 5% der
 * Gesamtspanne (fullMax - fullMin) - skaliert dadurch automatisch mit jeder Domaene
 * (Millisekunden bei Tagesdaten, rohe Kalenderjahre bei Jahresreihen), ohne einen
 * separaten Schrittweiten-Parameter zu brauchen. Ohne bestehende Auswahl (beide `null`)
 * legt der erste Tastendruck den jeweils GEGENUEBERLIEGENDEN Rand in der Mitte des
 * aktuell sichtbaren Bereichs an (z.B. Pfeil-rechts: links=Mitte, rechts=Mitte+Schritt) -
 * dieselbe Ausgangslage, die ein Mausklick in der Mitte des Charts erzeugen wuerde.
 */
export function computeKeyboardZoomStep(
  current: { left: number | null; right: number | null },
  visibleRange: [number, number] | null,
  fullMin: number,
  fullMax: number,
  direction: "left" | "right",
): { left: number; right: number } {
  const step = (fullMax - fullMin) / 20;
  const [visMin, visMax] = visibleRange ?? [fullMin, fullMax];
  const center = (visMin + visMax) / 2;
  let left = current.left ?? center;
  let right = current.right ?? center;
  if (direction === "right") right = Math.min(fullMax, right + step);
  else left = Math.max(fullMin, left - step);
  return { left, right };
}

/**
 * "Nice numbers"-Tick-Berechnung fuer numerische Y-Achsen (backlog.md -> Runde 5 Punkt 19):
 * Recharts' eigene Auto-Tick-Erzeugung haelt zwar den Schritt zwischen den unteren Ticks
 * gleichmaessig (z.B. 0/4/8/12), rundet den OBERSTEN Tick aber teils nicht mit, sondern legt
 * ihn exakt auf den Datenmaximalwert (z.B. 14,17 statt 16, oder 4,6 Mrd. statt 4,5 Mrd.) -
 * wirkt wie ein Sprung in der Schrittweite. Fix nach demselben Prinzip wie computeYearTicks/
 * computeMonthTicks: Ticks selbst berechnen (klassischer "nice numbers"-Algorithmus - Schritt
 * auf 1/2/2.5/5/10 × Zehnerpotenz runden) und explizit an Recharts' `ticks`-Prop uebergeben,
 * statt der Auto-Heuristik zu vertrauen. `forceZero=true` erzwingt 0 als Fixpunkt (Balken-
 * Charts, siehe die bestehende Y-Achse-startet-bei-0-Regel in CLAUDE.md); `false` laesst den
 * engeren, tatsaechlichen Datenbereich zu (Flaechen-/Linien-Charts, wo es um die Richtung
 * geht, nicht absolute Balkenhoehen - gleiche Unterscheidung wie beim bestehenden
 * bar-vs-auto-domain-Muster in historical-line-chart.tsx).
 */
export function computeNiceYAxisTicks(values: number[], forceZero: boolean, tickCount = 5): number[] {
  const finite = values.filter((v) => Number.isFinite(v));
  if (finite.length === 0) return [0];

  let min = Math.min(...finite);
  let max = Math.max(...finite);
  if (forceZero) {
    min = Math.min(0, min);
    max = Math.max(0, max);
  }
  if (min === max) {
    // Flache/einzelne Datenreihe - kuenstlicher Bereich, damit ueberhaupt mehrere Ticks
    // entstehen, statt einer einzelnen Linie ohne Skala.
    const pad = min === 0 ? 1 : Math.abs(min) * 0.1;
    min -= pad;
    max += pad;
  }

  const rawStep = (max - min) / Math.max(1, tickCount - 1);
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const normalized = rawStep / magnitude;
  const niceNormalized = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 2.5 ? 2.5 : normalized <= 5 ? 5 : 10;
  const step = niceNormalized * magnitude;

  const niceMin = forceZero ? 0 : Math.floor(min / step) * step;
  const niceMax = Math.ceil(max / step) * step;

  const ticks: number[] = [];
  for (let t = niceMin; t <= niceMax + step * 1e-6; t += step) {
    ticks.push(Math.round(t / step) * step);
  }
  return ticks;
}
